import React, {
  useState,
  useEffect,
  useRef,
} from "react";

import {
  Search,
  Barcode,
  Package2,
  DollarSign,
  X,
} from "lucide-react";

import "../css/productSearch.css";

const API_BASE =
  "https://pos-backend-58o9.onrender.com";

const ProductSearch = ({
  searchTerm,
  setSearchTerm,
  addToCart,
}) => {

  const [products, setProducts] =
    useState([]);

  const [filteredProducts, setFilteredProducts] =
    useState([]);

  const [showPriceModal, setShowPriceModal] =
    useState(false);

  const [selectedProduct, setSelectedProduct] =
    useState(null);

  const [customPrice, setCustomPrice] =
    useState("");

  const [highlightedIndex, setHighlightedIndex] =
    useState(-1);

  const inputRef = useRef(null);

  /* ========================================================
      LOAD PRODUCTS
  ======================================================== */
  useEffect(() => {

    fetch(`${API_BASE}/api/products`)
      .then((res) => res.json())
      .then((data) => setProducts(data))
      .catch((err) =>
        console.error(
          "Failed to load products",
          err
        )
      );

  }, []);

  /* ========================================================
      NORMALIZE PRODUCT
  ======================================================== */
  const normalizeForCart = (
    p,
    override = {}
  ) => ({
    ...p,

    product_quantity: Number(
      p.product_quantity ??
      p.quantity ??
      0
    ),

    available_quantity: Number(
      p.product_quantity ??
      p.quantity ??
      0
    ),

    uses_stock:
      p.uses_stock === true ||
      p.uses_stock === 1 ||
      String(
        p.uses_stock
      ).toLowerCase() === "yes",

    price: Number(p.price),

    ...override,
  });

  /* ========================================================
      SEARCH
  ======================================================== */
  useEffect(() => {

    if (!searchTerm) {
      setFilteredProducts([]);
      return;
    }

    const code = searchTerm.trim();

    /* =========================
        BARCODE
    ========================== */
    if (/^\d+$/.test(code)) {

      fetch(
        `${API_BASE}/api/products/barcode/${code}`
      )
        .then((res) =>
          res.ok ? res.json() : null
        )
        .then((data) => {

          if (!data) return;

          if (data.price_change_allowed) {

            setSelectedProduct(data);
            setCustomPrice(data.price);
            setShowPriceModal(true);

          } else {

            addToCart(
              normalizeForCart(data, {
                quantity: 1,
              })
            );
          }

          setSearchTerm("");
          setFilteredProducts([]);
        })
        .catch((err) =>
          console.error(
            "Barcode lookup failed:",
            err
          )
        );

    } else {

      /* =========================
          TEXT SEARCH
      ========================== */
      const lowerTerm =
        code.toLowerCase();

      const matches = products.filter(
        (p) =>
          p.name
            .toLowerCase()
            .includes(lowerTerm) ||
          p.code?.toLowerCase() ===
            lowerTerm ||
          p.barcode?.toLowerCase() ===
            lowerTerm
      );

      setFilteredProducts(matches);
    }

  }, [searchTerm, products]);

  /* ========================================================
      KEYBOARD NAVIGATION
  ======================================================== */
  useEffect(() => {

    const handleKeyDown = (e) => {

      if (
        document.activeElement !==
        inputRef.current
      )
        return;

      if (
        filteredProducts.length === 0
      )
        return;

      if (e.key === "ArrowDown") {

        e.preventDefault();

        setHighlightedIndex((prev) =>
          prev <
          filteredProducts.length - 1
            ? prev + 1
            : 0
        );

      } else if (
        e.key === "ArrowUp"
      ) {

        e.preventDefault();

        setHighlightedIndex((prev) =>
          prev > 0
            ? prev - 1
            : filteredProducts.length - 1
        );

      } else if (
        e.key === "Enter"
      ) {

        e.preventDefault();

        if (
          highlightedIndex >= 0 &&
          filteredProducts[
            highlightedIndex
          ]
        ) {
          handleSelectProduct(
            filteredProducts[
              highlightedIndex
            ]
          );
        }

      } else if (
        e.key === "Escape"
      ) {

        setFilteredProducts([]);
        setHighlightedIndex(-1);
      }
    };

    const input = inputRef.current;

    input?.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () =>
      input?.removeEventListener(
        "keydown",
        handleKeyDown
      );

  }, [
    filteredProducts,
    highlightedIndex,
  ]);

  /* ========================================================
      SELECT PRODUCT
  ======================================================== */
  const handleSelectProduct = (
    product
  ) => {

    if (!product) return;

    if (
      product.price_change_allowed
    ) {

      setSelectedProduct(product);

      setCustomPrice(product.price);

      setShowPriceModal(true);

    } else {

      addToCart(
        normalizeForCart(product, {
          quantity: 1,
        })
      );
    }

    setTimeout(() => {

      setSearchTerm("");
      setFilteredProducts([]);
      setHighlightedIndex(-1);

    }, 50);
  };

  /* ========================================================
      CONFIRM PRICE
  ======================================================== */
  const confirmCustomPrice = () => {

    if (
      !customPrice ||
      isNaN(customPrice)
    )
      return;

    addToCart(
      normalizeForCart(
        selectedProduct,
        {
          quantity: 1,
          price: Number(customPrice),
        }
      )
    );

    setShowPriceModal(false);

    setCustomPrice("");

    setSelectedProduct(null);

    setSearchTerm("");

    setFilteredProducts([]);
  };

  /* ========================================================
      ESC CLOSE
  ======================================================== */
  useEffect(() => {

    const handleEsc = (e) => {

      if (
        e.key === "Escape" &&
        showPriceModal
      ) {

        e.preventDefault();

        setShowPriceModal(false);

        setSelectedProduct(null);
      }
    };

    window.addEventListener(
      "keydown",
      handleEsc
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleEsc
      );

  }, [showPriceModal]);

useEffect(() => {

  if (showPriceModal) {
    document.body.classList.add(
      "modal-open"
    );
  } else {
    document.body.classList.remove(
      "modal-open"
    );
  }

  return () => {
    document.body.classList.remove(
      "modal-open"
    );
  };

}, [showPriceModal]);




  /* ========================================================
      UI
  ======================================================== */
  return (
    <div className="modern-product-search">

      {/* =========================
          SEARCH INPUT
      ========================== */}
      <div className="modern-search-input-wrap">

        <div className="modern-search-icon">
          <Search size={18} />
        </div>

        <input
          ref={inputRef}
          type="text"
          placeholder="Search product, barcode or code..."
          value={searchTerm}
          onChange={(e) =>
            setSearchTerm(
              e.target.value
            )
          }
          className="modern-search-input"
          autoFocus
        />

        <div className="modern-search-barcode">
          <Barcode size={18} />
        </div>

      </div>

      {/* =========================
          DROPDOWN
      ========================== */}
      {filteredProducts.length > 0 &&
        !showPriceModal && (

          <div className="modern-search-dropdown">

            {filteredProducts.map(
              (product, index) => (

                <button
                  key={product.id}
                  type="button"
                  onClick={() =>
                    handleSelectProduct(
                      product
                    )
                  }
                  className={`modern-dropdown-item ${
                    index ===
                    highlightedIndex
                      ? "active"
                      : ""
                  }`}
                >

                  <div className="modern-dropdown-left">

                    <div className="modern-dropdown-icon">
                      <Package2
                        size={16}
                      />
                    </div>

                    <div className="modern-dropdown-info">

                      <span className="modern-dropdown-name">
                        {product.name}
                      </span>

                      <span className="modern-dropdown-meta">
                        {product.code ||
                          "No Code"}
                      </span>

                    </div>

                  </div>

                  <div className="modern-dropdown-price">
                    $
                    {parseFloat(
                      product.price
                    ).toFixed(2)}
                  </div>

                </button>
              )
            )}

          </div>
        )}

      {/* =========================
          CUSTOM PRICE MODAL
      ========================== */}
      {showPriceModal && (

        <div className="modern-price-modal-overlay">

          <div className="modern-price-modal">

            <button
              className="modern-modal-close"
              onClick={() => {
                setShowPriceModal(
                  false
                );

                setSelectedProduct(
                  null
                );
              }}
            >
              <X size={18} />
            </button>

            <div className="modern-price-modal-header">

              <div className="modern-price-modal-icon">
                <DollarSign
                  size={22}
                />
              </div>

              <div>

                <h3>
                  Custom Price
                </h3>

                <p>
                  {
                    selectedProduct?.name
                  }
                </p>

              </div>

            </div>

            <form
              className="modern-price-form"
              onSubmit={(e) => {
                e.preventDefault();
                confirmCustomPrice();
              }}
            >

              <input
                type="number"
                placeholder="Enter price"
                value={customPrice}
                onChange={(e) =>
                  setCustomPrice(
                    e.target.value
                  )
                }
                className="modern-price-input"
                autoFocus
              />

              <div className="modern-price-actions">

                <button
                  type="button"
                  className="modern-price-cancel"
                  onClick={() => {
                    setShowPriceModal(
                      false
                    );

                    setSelectedProduct(
                      null
                    );
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="modern-price-confirm"
                >
                  Add To Cart
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </div>
  );
};

export default ProductSearch;